const LOW_EVENT_BINARY_UNDER_STATS = [
  "hits",
  "singles",
  "runs",
  "walks",
  "rbis",
  "hits+runs+rbis",
  "stolen_bases",
];

const HIGH_RISK_LINE_STATS = [
  "hits",
  "singles",
  "runs",
  "walks",
  "rbis",
  "hits+runs+rbis",
  "stolen_bases",
  "hitter_ks",
];

const MLB_STACKED_UNDER_STATS = ["singles", "hits", "walks", "runs"];

const STAT_ALIASES = {
  hit: "hits",
  single: "singles",
  run: "runs",
  walk: "walks",
  bb: "walks",
  base_on_balls: "walks",
  bases_on_balls: "walks",
  rbi: "rbis",
  runs_batted_in: "rbis",
  runs_batted_ins: "rbis",
  stolen_base: "stolen_bases",
  stolen_bases: "stolen_bases",
  steals: "stolen_bases",
  sb: "stolen_bases",
  hrr: "hits+runs+rbis",
  "hits+runs+rbi": "hits+runs+rbis",
  hits_runs_rbis: "hits+runs+rbis",
  hits_runs_rbi: "hits+runs+rbis",
  hit_runs_rbis: "hits+runs+rbis",
};

const LEAGUE_ALIASES = {
  baseball: "mlb",
  major_league_baseball: "mlb",
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const normalizeStatKey = (value) => {
  const raw = normalizeText(value)
    .replace(/^(projected_|promo_|goblin_|demon_)/, "")
    .replace(/\s+/g, "_")
    .replace(/\//g, "+")
    .replace(/-/g, "_");

  return STAT_ALIASES[raw] || raw;
};

const normalizeLeague = (value) => {
  const league = normalizeText(value).replace(/\s+/g, "_").replace(/-/g, "_");
  return LEAGUE_ALIASES[league] || league;
};

const normalizeDirection = (value) => {
  const direction = normalizeText(value);

  if (["more", "over", "o", "higher", "yes"].includes(direction)) {
    return "more";
  }

  if (["less", "under", "u", "lower", "no"].includes(direction)) {
    return "less";
  }

  return direction;
};

const parseLine = (leg) => {
  const value = leg?.line ?? leg?.projection ?? leg?.stat_value ?? leg?.stat;
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number.parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const isLineValue = (line, target) =>
  Number.isFinite(line) && Math.abs(line - target) < 0.000001;

const getLegId = (leg, index) => {
  const id = leg?.leg_id ?? leg?.legId ?? leg?.id ?? leg?.selection_id ?? leg?.selectionId;
  if (id !== null && id !== undefined && id !== "") {
    return String(id);
  }

  const athleteId = leg?.athlete_id ?? leg?.athleteId ?? leg?.player_id ?? leg?.playerId ?? leg?.name;
  const stat = leg?.stat_key ?? leg?.statType ?? leg?.stat_type ?? leg?.market ?? leg?.market_type;

  if (athleteId || stat) {
    return [athleteId || `leg-${index + 1}`, stat || ""].filter(Boolean).join(":");
  }

  return `leg-${index + 1}`;
};

const getStatType = (leg) => {
  const statCandidates = [
    leg?.statType,
    leg?.stat_type,
    leg?.market,
    leg?.market_type,
    leg?.market_key,
    leg?.marketName,
    leg?.market_name,
    leg?.stat_key,
    leg?.projection_type,
    leg?.type,
    typeof leg?.stat === "string" && !Number.isFinite(Number.parseFloat(leg.stat)) ? leg.stat : "",
  ];

  const statValue = statCandidates.find((value) => typeof value === "string" && value.trim() !== "");
  if (statValue) {
    return normalizeStatKey(statValue);
  }

  return "";
};

const getSelectionDirection = (leg) =>
  normalizeDirection(
    leg?.selected ?? leg?.selection ?? leg?.direction ?? leg?.pick_type ?? leg?.side ?? leg?.choice
  );

export const normalizePropCombinabilityLeg = (leg, index = 0) => ({
  raw: leg,
  id: getLegId(leg, index),
  sport: normalizeLeague(leg?.sport || leg?.league),
  league: normalizeLeague(leg?.league || leg?.sport),
  statType: getStatType(leg),
  line: parseLine(leg),
  direction: getSelectionDirection(leg),
  playerId: leg?.player_id ?? leg?.playerId ?? leg?.athlete_id ?? leg?.athleteId ?? "",
  gameId: leg?.game_id ?? leg?.gameId ?? leg?.event_id ?? leg?.eventId ?? "",
});

const isFantasyStyleComboProp = (leg) =>
  leg.statType.includes("fantasy") ||
  leg.statType.includes("combo") ||
  leg.statType === "fantasy_points" ||
  leg.statType === "fantasy_score";

const isBinaryUnderProp = (leg) =>
  isLineValue(leg.line, 0.5) &&
  leg.direction === "less" &&
  LOW_EVENT_BINARY_UNDER_STATS.includes(leg.statType);

const isHighRiskHalfPointProp = (leg) => {
  if (isLineValue(leg.line, 0.5) && HIGH_RISK_LINE_STATS.includes(leg.statType)) {
    return true;
  }

  return isFantasyStyleComboProp(leg) && Number.isFinite(leg.line) && leg.line <= 1.5;
};

const isMlbStackedUnderProp = (leg) =>
  leg.league === "mlb" &&
  isLineValue(leg.line, 0.5) &&
  leg.direction === "less" &&
  MLB_STACKED_UNDER_STATS.includes(leg.statType);

const buildError = ({ code, message, ruleName, legs }) => ({
  code,
  message,
  ruleName,
  legIds: legs.map((leg) => leg.id),
});

export const PROP_COMBINABILITY_RULES = {
  maxBinaryUnder: "max_binary_under_prop",
  maxHighRiskHalfPointProps: "max_high_risk_half_point_props",
  mlbStackedUnders: "mlb_stacked_half_point_unders",
};

export const validatePropCombinability = (entryLegs = [], userRiskTier = "recreational") => {
  const normalizedLegs = (Array.isArray(entryLegs) ? entryLegs : [])
    .map((leg, index) => normalizePropCombinabilityLeg(leg, index))
    .filter((leg) => leg.statType && Number.isFinite(leg.line));
  const errors = [];

  const binaryUnders = normalizedLegs.filter(isBinaryUnderProp);
  if (binaryUnders.length > 1) {
    errors.push(buildError({
      code: "prop_combinability_max_binary_under",
      message: "Only one 0.5 under prop is allowed per entry.",
      ruleName: PROP_COMBINABILITY_RULES.maxBinaryUnder,
      legs: binaryUnders,
    }));
  }

  const highRiskHalfPointProps = normalizedLegs.filter(isHighRiskHalfPointProp);
  if (highRiskHalfPointProps.length > 2) {
    errors.push(buildError({
      code: "prop_combinability_max_high_risk_0_5",
      message: "This entry includes too many low-event 0.5 props. Please remove one to continue.",
      ruleName: PROP_COMBINABILITY_RULES.maxHighRiskHalfPointProps,
      legs: highRiskHalfPointProps,
    }));
  }

  const mlbStackedUnders = normalizedLegs.filter(isMlbStackedUnderProp);
  if (mlbStackedUnders.length > 1) {
    errors.push(buildError({
      code: "prop_combinability_mlb_0_5_under_stack",
      message: "Multiple MLB 0.5 under props for hits, singles, walks, or runs cannot be combined.",
      ruleName: PROP_COMBINABILITY_RULES.mlbStackedUnders,
      legs: mlbStackedUnders,
    }));
  }

  return {
    valid: errors.length === 0,
    userRiskTier,
    errors,
  };
};
